const { devicesToProcess, deviceName } = require('./util/device')
const automaticReports = require('./automaticReports')
const traccarHelper = require('./util/traccar')
const { getNearestPOIs, insideGeofence, loadGroupsData } = require('./util/geofence')
const {
  getTranslations, convertToLocaleString, convertMS,
  getLanguage
} = require('./util/utils')
const jsPDF = require('jspdf')
const { headerFromUser, addTable } = require('./util/pdfDocument')
const { getStyle } = require('./reportStyle')
const { getUserPartner } = require('fleetmap-partners')
const { calculateStopDuration } = require('./util/trips')

const fileName = 'StopReport'

async function createStopReport (from, to, userData, traccar) {
  console.log('Create StopReport')
  const reportData = []

  const report = await createStopReportByDevice(from, to, userData, traccar)
  reportData.push(report)

  return reportData
}

async function createStopReportByDevice (from, to, userData, traccar) {
  const devices = devicesToProcess(userData)

  const allData = {
    devices: []
  }

  await loadGroupsData(userData, traccar)

  const sliced = automaticReports.sliceArray(devices, 5)

  let deviceCount = 0
  for (const slice of sliced) {
    const allInOne = await traccarHelper.getAllInOne(traccar, from, to, slice, true, false, true, false, deviceCount, devices.length)
    console.log('Stops:' + allInOne.stops.length)

    if (allInOne.stops.length > 0) {
      allData.devices.push(...processDevices(from, to, slice, allInOne, userData))
    }
    deviceCount = deviceCount + slice.length
  }

  return allData
}

function processDevices (from, to, devices, data, userData) {
  const devicesResult = []

  devices.forEach(d => {
    const deviceStops = data.stops.filter(t => t.deviceId === d.id)
    const route = data.route.filter(t => t.deviceId === d.id)

    deviceStops.forEach(stop => {
      stop.stopDuration = calculateStopDuration(stop)
    })

    if (deviceStops.length && route.length && !route[0].attributes.ignition) {
      deviceStops[0].startTime = undefined
    }

    const filteredStops = deviceStops.filter(s => s.stopDuration > (userData.minimumStopMinutes ? userData.minimumStopMinutes * 60 * 1000 : 0))

    filteredStops.forEach(stop => {
      const routeInsideGeofence = route.filter(p =>
        new Date(p.fixTime).getTime() >= new Date(stop.startTime).getTime() &&
          new Date(p.fixTime).getTime() <= new Date(stop.endTime).getTime())

      const nearestPOIs = getNearestPOIs(stop.longitude, stop.latitude, userData.geofences)
      if (nearestPOIs.length > 0) {
        stop.endPOIName = nearestPOIs[0].p.name
      }

      routeInsideGeofence.every(p => {
        const geofence = userData.geofences.filter(g => g && g.area.startsWith('POLYGON')).find(g =>
          insideGeofence({ latitude: p.latitude, longitude: p.longitude }, g)
        )
        if (geofence) {
          const group = userData.groups.find(g => g.geofenceIds && g.geofenceIds.includes(geofence.id))
          stop.geofenceName = geofence.name
          stop.geofenceGroup = (group && group.name) || ''
          return false
        }
        return true
      })
    })

    if (filteredStops.length > 0) {
      const deviceData = {
        device: d,
        from,
        to,
        stops: filteredStops
      }

      devicesResult.push(deviceData)
    }
  })

  return devicesResult
}

async function exportStopReportToPDF (userData, reportData) {
  console.log('Export stop report to PDF')

  const timezone = userData.user.attributes.timezone
  const translations = getTranslations(userData)
  const lang = getLanguage(userData)
  const stopsData = userData.byDriver ? reportData.drivers : reportData.devices

  const headers = [
    translations.report.start,
    translations.report.end,
    translations.report.address,
    translations.report.idleTime,
    translations.report.event_ignitionOff,
    translations.report.zone
  ]

  if (stopsData) {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF.jsPDF('l')
    await headerFromUser(doc, translations.report.titleTripReport, userData.user)

    stopsData.forEach(function (d, index) {
      const data = []
      const name = deviceName(d.device)
      const group = userData.groups.find(g => d.device.groupId === g.id)

      const space = index === 0 ? 8 : 0
      if (index) {
        doc.addPage()
      }

      doc.setFontSize(13)
      doc.text(name, 20, space + 20)
      doc.setFontSize(11)
      doc.text(group ? translations.report.group + ': ' + group.name : '', 200, space + 20)
      doc.text(convertToLocaleString(d.from, lang, timezone) + ' - ' + convertToLocaleString(d.to, lang, timezone), 20, space + 25)
      doc.text(translations.report.minimumMinutes + ': ' + userData.minimumStopMinutes + ' ' + translations.report.minutes, 20, space + 35)

      d.stops.forEach(a => {
        data.push([
          getStopStart(userData.user, a),
          getStopEnd(userData.user, a),
          a.endPOIName || a.address,
          convertMS(a.engineHours, true),
          convertMS(a.stopDuration, true),
          (a.geofenceName || '') + (a.geofenceGroup ? ' (' + a.geofenceGroup + ')' : '')
        ])
      })

      const footValues = [
        'Total:' + d.stops.length,
        '', '', '',
        convertMS(d.stops.reduce((a, b) => a + b.engineHours, 0), true),
        convertMS(d.stops.reduce((a, b) => a + b.stopDuration, 0), true)]

      const style = getStyle(getUserPartner(userData.user))
      addTable(doc,
        headers, data, footValues, style,
        space + 45,
        {
          4: { halign: 'right' },
          5: { halign: 'right' }
        })
    })
    return doc
  }
}

function exportStopReportToExcel (userData, reportData) {
  console.log('Export stop report to Excel')

  const translations = getTranslations(userData)

  const settings = {
    sheetName: translations.report.titleStopReport, // The name of the sheet
    fileName // The name of the spreadsheet
  }

  const headers = [
    { label: translations.report.vehicle, value: 'name' },
    { label: translations.report.start, value: 'start' },
    { label: translations.report.end, value: 'end' },
    { label: translations.report.address, value: 'address' },
    { label: translations.report.idleTime, value: 'idleTime' },
    { label: translations.report.event_ignitionOff, value: 'stopTime' },
    { label: translations.report.zone, value: 'zone' }
  ]

  let data = []
  if (reportData.devices) {
    reportData.devices.forEach(d => {
      data = data.concat([{}])
      data = data.concat(d.stops.map(a => {
        return {
          name: a.deviceName,
          start: getStopStart(userData.user, a),
          end: getStopEnd(userData.user, a),
          address: a.endPOIName || a.address,
          idleTime: convertMS(a.engineHours, true),
          stopTime: convertMS(a.stopDuration, true),
          zone: (a.geofenceName || '') + (a.geofenceGroup ? ' (' + a.geofenceGroup + ')' : '')
        }
      }))
      // Totals
      data = data.concat([{
        name: d.stops.length,
        idleTime: convertMS(d.stops.reduce((a, b) => a + b.engineHours, 0), true),
        stopTime: convertMS(d.stops.reduce((a, b) => a + b.stopDuration, 0), true)
      }])
      data = data.concat([{}])
    })
    console.log(data)
    return {
      headers,
      data,
      settings
    }
  }
}

function getStopStart (user, stop) {
  return stop.startTime ? convertToLocaleString(stop.startTime, user.attributes.lang, user.attributes.timezone) : ''
}

function getStopEnd (user, stop) {
  return stop.endTime ? convertToLocaleString(stop.endTime, user.attributes.lang, user.attributes.timezone) : ''
}

exports.createStopReport = createStopReport
exports.exportStopReportToPDF = exportStopReportToPDF
exports.exportStopReportToExcel = exportStopReportToExcel
exports.create = createStopReport
exports.exportToPDF = exportStopReportToPDF
exports.exportToExcel = exportStopReportToExcel
